import { useMemo, useEffect } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import { Button } from '@mui/material'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

/**
 * BillingAddressDetails Component
 *
 * A form component that collects billing address information in the user edit workflow.
 * This component uses Formik for form state management and validation.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.payload - Initial billing address data
 * @param {Object} [props.payload.billingAddress] - Billing address information
 * @param {string} [props.payload.billingAddress.name] - Owner of the billing address
 * @param {string} [props.payload.billingAddress.street] - Street name
 * @param {string} [props.payload.billingAddress.houseNumber] - House number
 * @param {string} [props.payload.billingAddress.city] - City name
 * @param {string} [props.payload.billingAddress.postalCode] - Postal code
 * @param {Function} props.setCanNext - Function to enable/disable the next button in parent stepper
 * @param {Function} props.setPayload - Function to update the parent component's state with form data
 * @param {Function} props.confirmPayload - Function to be called after successful form submission
 * @param {number} props.submit - Trigger for form submission from parent
 * @param {Function} props.setSubmit - Function to reset the submission trigger
 * @returns {React.ReactElement} The rendered form component
 */
function BillingAddressDetails({
  payload,
  setCanNext,
  setPayload,
  confirmPayload,
  submit,
  setSubmit,
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (submit > 0) document.getElementById('subBilling').click()
    setSubmit(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  const initialValues = {
    name: payload?.billingAddress?.name || '',
    street: payload?.billingAddress?.street || '',
    houseNumber: payload?.billingAddress?.houseNumber || '',
    city: payload?.billingAddress?.city || '',
    postalCode: payload?.billingAddress?.postalCode || '',
  }
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required(t('LBLBillingAddressOwnerReq'))
          .label(t('LBLBillingAddressOwner')),
        street: Yup.string().required('required').label(t('lblStName')),
        houseNumber: Yup.string()
          .required(t('LBLhouseNrReq'))
          .label(t('LBLHouseNr')),
        city: Yup.string().required(t('LBLLocReq')).label(t('city')),
        postalCode: Yup.number()
          .typeError(t('NumericOnly'))
          .required(t('ZipcodeReq'))
          .label(t('PostalCode')),
      }),
    [t]
  )
  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      billingAddress: {
        name: values.name,
        city: values.city,
        street: values.street,
        houseNumber: values.houseNumber,
        postalCode: values.postalCode,
      },
    }

    setPayload(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        {({ touched, isValid }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="name"
                  name="name"
                  label={t('LBLBillingAddressOwner')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  required
                />
              </Stack>
              <Stack my={5} direction="row" alignItems="center" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="street"
                  name="street"
                  label={t('lblStName')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="houseNumber"
                  name="houseNumber"
                  label={t('LBLHouseNr')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  required
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="postalCode"
                  name="postalCode"
                  label={t('PostalCode')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  required
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="city"
                  name="city"
                  label={t('city')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>

              <Button
                sx={{ display: 'none' }}
                id="subBilling"
                type="submit"
              ></Button>
            </>
          </Form>
        )}
      </Formik>
    </>
  )
}

BillingAddressDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayload: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default BillingAddressDetails
